/**
 * useTour Hook - Manages guided tour state based on URL parameters
 * Supports ?tour, ?firstvisit, ?admin-tour query parameters
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export interface TourStep {
  id: string;
  target: string;  // CSS selector
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  superAdminOnly?: boolean;  // Only show this step to super admins
  action?: () => void;  // Optional action to run before showing this step
}

export interface UseTourOptions {
  steps: TourStep[];
  isSuperAdmin?: boolean;
}

export const useTour = ({ steps, isSuperAdmin = false }: UseTourOptions) => {
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState<TourStep[]>([]);

  // Check URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasTour = params.has('tour') || params.has('firstvisit') || params.has('admin-tour');
    const isAdminTour = params.has('admin-tour');
    
    // If admin-tour, only activate for super admins
    if (isAdminTour && !isSuperAdmin) {
      console.log('[useTour] ⏸️  Admin tour requested, waiting for admin status...');
      return;
    }

    if (hasTour) {
      console.log('[useTour] ✅ Activating tour from URL params');
      setIsActive(true);
      setCurrentStep(0);
    }
  }, [location.search, isSuperAdmin]);  // Use location.search specifically, not location

  // Filter steps based on super admin status
  useEffect(() => {
    const filtered = steps.filter(step => {
      if (step.superAdminOnly && !isSuperAdmin) {
        return false;
      }
      return true;
    });
    if (filtered.length !== visibleSteps.length) {
      console.log(`[useTour] Filtered steps: ${filtered.length}/${steps.length} (admin: ${isSuperAdmin})`);
    }
    setVisibleSteps(filtered);
  }, [steps, isSuperAdmin]);

  const getCurrentStep = (): TourStep | null => {
    return visibleSteps[currentStep] || null;
  };

  const goToNextStep = () => {
    if (currentStep < visibleSteps.length - 1) {
      const nextStep = currentStep + 1;
      console.log('[useTour] Going to next step:', nextStep, '/', visibleSteps.length);
      setCurrentStep(nextStep);
    } else {
      console.log('[useTour] At last step, cannot advance');
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      console.log('[useTour] Going to previous step:', prevStep, '/', visibleSteps.length);
      setCurrentStep(prevStep);
    } else {
      console.log('[useTour] At first step, cannot go back');
    }
  };

  const completeTour = () => {
    console.log('[useTour] Tour completed');
    setIsActive(false);
    setCurrentStep(0);
    // Remove tour from URL
    const params = new URLSearchParams(location.search);
    params.delete('tour');
    params.delete('firstvisit');
    params.delete('admin-tour');
    const newSearch = params.toString();
    window.history.replaceState({}, '', `${location.pathname}${newSearch ? '?' + newSearch : ''}`);
  };

  const startTour = () => {
    console.log('[useTour] ▶️ Starting tour from UI action');
    setIsActive(true);
    setCurrentStep(0);
  };

  return {
    isActive,
    currentStep,
    totalSteps: visibleSteps.length,
    currentStepData: getCurrentStep(),
    startTour,
    goToNextStep,
    goToPreviousStep,
    completeTour,
    steps: visibleSteps,
  };
};
